import {scope, type} from 'arktype'

import {responseData} from '@/lib/api/ResponseData'
import {fallbackToDefault} from '@/utils/schema/fallback'
import filteredArray from '@/utils/schema/filteredArray'

import {FoodSearchFocus, FoodSort, FoodStoreType} from './types'

export const types = scope({
  ConditionalPrice: {
    'name?': 'string',
    'min_choice_options?': 'number',
    'max_choice_options?': 'number',
    'options?': 'Option[]',
    'customization_id?': 'string',
  },
  BaseOption: {
    default_qty: 'number',
    name: 'string',
    option_id: 'string',
    price: 'number',
  },
  Option: {
    '...': 'BaseOption',
    'customizations?': 'Customization[]',
    'min_qty': 'number',
    'max_qty': 'number',
    'conditional_price?': 'ConditionalPrice | null',
    'formatted_price': 'string',
  },
  BaseCustomization: {
    customization_id: 'string',
    name: 'string',
  },
  Customization: {
    '...': 'BaseCustomization',
    'min_choice_options': 'number',
    'max_choice_options': 'number',
    'options?': 'Option[]',
  },
  SelectedOption: {
    '...': 'BaseOption',
    'customizations?': 'SelectedCustomization[]',
    'quantity': 'number',
  },
  SelectedCustomization: {
    '...': 'BaseCustomization',
    'selected_options': 'SelectedOption[]',
  },
}).export()

export type Customization = typeof types.Customization.infer
export type Option = typeof types.Option.infer
export type ConditionalPrice = typeof types.ConditionalPrice.infer

export const SmallOrderFee = type({
  minimum_order_value: 'number',
  small_order_fee_flat: 'number',
  small_order_fee_percent: 'number',
})

export const ServiceFee = type({
  'service_fee_flat?': 'number',
  'service_fee_percent?': 'number',
  'service_fee_min?': 'number',
  'service_fee_max?': 'number',
  'service_fee_taxable?': 'boolean',
})

export const ThresholdFree = type({
  threshold_cents: 'number',
  threshold_fee_cents: 'number',
})

export const DeliveryFee = type({
  'delivery_fee_flat': 'number',
  'delivery_fee_percent': 'number',
  'delivery_fee_taxable': 'boolean',
  'threshold_fees?': ThresholdFree.array(),
})

export const Scheduled = type({
  id: 'string',
  minimum: 'number',
  maximum: 'number',
  delivery_fee: DeliveryFee,
  service_fee: ServiceFee,
})

export type Scheduled = typeof Scheduled.infer

export const TimeEstimate = type({
  'minimum': 'number',
  'maximum': 'number',
  'scheduled?': Scheduled.array(),
})

export const DeliveryMethod = type({
  time_estimate: TimeEstimate,
  sales_tax_percent: 'number',
  order_minimum: 'number',
  delivery_fee: DeliveryFee,
  service_fee: ServiceFee,
  small_order_fee: SmallOrderFee,
})

export const Quote = type({
  '...': DeliveryMethod,
  'accepts_delivery_tip': 'boolean',
  'accepts_pickup_tip': 'boolean',
  'accepts_pickup_note': 'boolean',
  'asap_available': 'boolean',
  'first_party_quote': 'boolean',
  'third_party_quote': 'boolean',
  'courier_quote': 'boolean',
  'order_maximum?': 'number | null',
  'order_minimum': 'number',
})

export type Quote = typeof Quote.infer

export const MenuItemList = type({
  'name': 'string',
  'price': 'number',
  'qty_available': 'number | null',
  'unit_size': 'number | null',
  'unit_of_measurement': 'string|null',
  'description': 'string',
  'image?': 'string | null',
  'min_price': 'number',
  'original_price': 'number',
  'formatted_price': 'string',
  'attributes': 'string[]',
  'product_id': 'string',
  'thumbnail_image?': 'string | null',
  'should_fetch_customizations': 'boolean',
  'supports_image_scaling': 'boolean',
  'customizations?': types.Customization.array(),
})

export type MenuItemList = typeof MenuItemList.infer

export const FoodProduct = type({
  '...': MenuItemList.omit('attributes', 'should_fetch_customizations'),
  'upc_code?': type('string | null'),
  'is_alcoholic': 'boolean',
  'is_available': 'boolean',
  'default_customization_description?': 'string',
})

export type FoodProduct = typeof FoodProduct.infer

export const Category = type({
  name: 'string',
  subcategory_id: 'string',
  menu_item_list: MenuItemList.array(),
})

export type Category = typeof Category.infer

export const StoreDetail = type({
  menu_id: 'string',
  categories: Category.array(),
  quote: Quote,
})

export type StoreDetail = typeof StoreDetail.infer

export const StoreDetailParams = type({
  'store_id': 'string',
  'pickup?': 'boolean',
  'include_quote?': 'boolean',
  'quote_preference?': 'string',
  'include_customizations?': 'boolean',
  'user_latitude': 'number',
  'user_longitude': 'number',
  'user_street_num': 'string',
  'user_street_name': 'string',
  'user_city': 'string',
  'user_state': 'string',
  'user_zipcode': 'string',
  'user_country': 'string',
  'image_height?': 'number',
  'image_width?': 'number',
  'available?': 'boolean',
  'include_fees?': 'boolean',
  'include_menu?': 'boolean',
})
export type StoreDetailParams = typeof StoreDetailParams.infer

export const Address = type({
  street_addr: 'string',
  city: 'string',
  state: 'string',
  zipcode: 'string',
  country: 'string',
  street_addr_2: 'string',
  latitude: 'number',
  longitude: 'number',
})
export type Address = typeof Address.infer

export const UserInfo = type({
  lat: 'number',
  lng: 'number',
  address: 'string',
  user_street_num: 'string',
  user_street_name: 'string',
  user_city: 'string',
  user_state: 'string',
  user_zipcode: 'string',
  user_country: 'string',
})
export type UserInfo = typeof UserInfo.infer

export const StoreIdProps = type({
  '...': UserInfo,
  'id': 'string',
  'store_description': 'string',
  'store_rating?': 'number',
  'store_rating_count?': 'number',
  'store_address': Address,
  'store_cuisine': 'string[]',
  'store_name': 'string',
  'store_delivery_time?': 'number',
  'store_delivery_distance?': 'number',
  'store_image': 'string[]',
  'pickup_enabled': 'boolean',
  'delivery_enabled': 'boolean',
})

export type StoreIdProps = typeof StoreIdProps.infer

export const selectionTreeType = scope({
  SelectionTree: {
    'option_id': 'string',
    'quantity?': 'number',
    'children?': 'SelectionTree[]',
  },
}).export()

export type SelectionTree = typeof selectionTreeType.SelectionTree.infer

export const Payload = type({
  'product_id': 'string',
  // quantity?: number;
  'options?': selectionTreeType.SelectionTree.array(),
})

export type Payload = typeof Payload.infer

export const FinalPayload = type({
  product_id: 'string',
  quantity: 'number',
  customizations: Payload.array(),
})

export type FinalPayload = typeof FinalPayload.infer

export const Dates = type({
  Monday: 'string',
  Tuesday: 'string',
  Wednesday: 'string',
  Thursday: 'string',
  Friday: 'string',
  Saturday: 'string',
  Sunday: 'string',
})

export type Dates = typeof Dates.infer

export const LocalHours = type({
  operational: Dates,
  delivery: Dates,
  pickup: Dates,
  dine_in: Dates,
})

export const Hours = type({
  '[string]': {
    'operational?': Dates,
    'delivery?': Dates,
    'pickup?': Dates,
    'dine_in?': Dates,
  },
})

export type Hours = typeof Hours.infer

export const Quotes = type({
  'cheapest_delivery?': DeliveryMethod,
  'fastest_delivery?': DeliveryMethod,
  'pickup?': DeliveryMethod,
})

export const Store = type({
  '_id': 'string',
  'name': 'string',
  'phone_number?': 'number | null',
  'address': Address,
  'description': 'string|null',
  'local_hours': LocalHours,
  'hours': Hours,
  'cuisines': 'string[]',
  'food_photos': 'string[]',
  'logo_photos': 'string[]',
  'store_photos': 'string[]',
  'dollar_signs?': 'number | null',
  'pickup_enabled': 'boolean',
  'delivery_enabled': 'boolean',
  'is_open': 'boolean',
  'quotes?': Quotes,
  'offers_first_party_delivery?': 'boolean',
  'offers_third_party_delivery?': 'boolean',
  'quote_ids?': 'string[]',
  'miles?': 'number',
  'weighted_rating_value?': 'number',
  'aggregated_rating_count?': 'number',
  'supports_upc_codes?': 'boolean',
  'type?': 'string',
})

export type Store = typeof Store.infer

export const StoreResponse = type({
  stores: Store.array(),
})

export const StoreDetailsResponse = type({
  store: Store.omit('miles'),
})

export const StoreSearchParams = type({
  'query?': 'string',
  'latitude': 'number',
  'longitude': 'number',
  'store_type?': type.enumerated(...Object.values(FoodStoreType)).configure({
    default: FoodStoreType[''],
  }),
  'user_street_num?': 'string',
  'user_street_name?': 'string',
  'user_city?': 'string',
  'user_state?': 'string',
  'user_zipcode?': 'string',
  'user_country?': 'string',
  'pickup?': 'boolean',
  'fetch_quotes?': 'boolean',
  'timeout?': 'number',
  'sort?': type.enumerated(...Object.values(FoodSort)).configure({
    default: FoodSort.Relevance,
  }),
  'open?': 'boolean',
  'search_focus?': type.enumerated(...Object.values(FoodSearchFocus)).configure({
    default: FoodSearchFocus.Store,
  }),
  'default_quote?': 'boolean',
  'include_utc_hours?': 'boolean',
  'use_new_db?': 'boolean',
})

export type StoreSearchParams = typeof StoreSearchParams.infer

export const AddressToGeoCode = type({
  lat: 'number',
  lng: 'number',
})

export const AddressResponse = type({
  '...': AddressToGeoCode,
  'address': 'string',
})

export const AddressComponents = type({
  'street_num?': 'string',
  'street_name?': 'string',
  'city?': 'string',
  'zipcode?': 'string',
  'state?': 'string',
  'country?': 'string',
})

export const GeoCodeToAddress = type({
  formatted_address: 'string',
  address_components: AddressComponents,
})

export type GeoCodeToAddress = typeof GeoCodeToAddress.infer

export const PlaceAutocompleteParams = type({
  'search': 'string',
  'location_bias_latitude?': 'number',
  'location_bias_longitude?': 'number',
  'location_bias_radius?': 'number',
  'search_type?': 'string',
  'open_filter?': 'boolean',
})
export type PlaceAutocompleteParams = typeof PlaceAutocompleteParams.infer

export const Place = type({
  google_place_id: 'string',
  place_address: 'string',
})
export type Place = typeof Place.infer

export const PlaceAutocomplete = type({
  places: Place.array(),
  sessiontoken: 'string',
})
export type PlaceAutocomplete = typeof PlaceAutocomplete.infer

export const FoodCartItem = type({
  productId: 'string',
  selectedOptions: fallbackToDefault(
    filteredArray(
      type({
        optionId: 'string',
        quantity: 'number',
      }),
    ),
    [],
  ),
  storeId: 'string',
  quantity: 'number',
  notes: 'string?',
})

export type FoodCartItem = typeof FoodCartItem.infer

export const FoodCartV1 = type({
  foodCartItems: fallbackToDefault(filteredArray(FoodCartItem), []),
  version: 'number',
})

export type FoodCartV1 = typeof FoodCartV1.infer

export function getDefaultFoodCart(): FoodCartV1 {
  return {
    foodCartItems: [],
    version: 1,
  }
}

export const AddedFees = type({
  flat_fee_cents: 'number',
  percent_fee: 'number',
  is_fee_taxable: 'boolean',
  total_fee_cents: 'number',
  sales_tax_cents: 'number',
})

export const MiscFees = type({
  fee_name: 'string',
  fee_amount: 'number',
})

export const FoodCartItemOriginal = type({
  name: 'string',
  base_price: 'number',
  quantity: 'number',
  description: 'string?',
  product_id: 'string',
  customizations: types.SelectedCustomization.array().optional(),
})
export type FoodCartItemOriginal = typeof FoodCartItemOriginal.infer

export const FinalQuote = type({
  store: 'string',
  store_address: 'string',
  store_id: 'string',
  quote_id: 'string',
  quote: {
    subtotal: 'number',
    delivery_fee_cents: 'number',
    service_fee_cents: 'number',
    small_order_fee_cents: 'number',
    sales_tax_cents: 'number',
    delivery_time_min: 'number',
    delivery_time_max: 'number',
    total_without_tips: 'number',
    expected_time_of_arrival: 'string',
    scheduled: Scheduled.or(type({})),
  },
  items: FoodCartItemOriginal.array(),
  tip: 'number',
  total_with_tip: 'number',
  added_fees: AddedFees,
  misc_fees: MiscFees.array(),
})

export const OrderInformation = type({
  order_id: 'string',
  order_placed: 'boolean',
  final_quote: FinalQuote,
})

export const MealMeOrderError = type({
  error: 'string',
})
export type MealMeOrderError = typeof MealMeOrderError.infer

export const MealMeProductUnavailableError = type({
  '...': MealMeOrderError,
  'invalid_product_ids': 'string[]',
})
export type MealMeProductUnavailableError = typeof MealMeProductUnavailableError.infer

export const MealMeCheckOutError = type({
  '...': MealMeOrderError,
  'invalid_option_ids': 'string[]',
})
export type MealMeCheckOutError = typeof MealMeCheckOutError.infer

export const FoodCartPreviewError = type({
  error: 'string',
})
export type FoodCartPreviewError = typeof FoodCartPreviewError.infer

export const OrderTracking = type({
  trackingLink: 'string?',
  etaTimestamp: 'number?',
  timestamp: 'number?',
  status: 'string',
  // isReady: 'boolean',
  driverLatitude: 'number?',
  driverLongitude: 'number?',
  course: 'number?',
  lastUpdateTimestamp: 'number?',
  completionTimestamp: 'number?',
  vehicle: 'string?',
  vehicleType: 'string?',
  driverName: 'string?',
  driverPhone: 'number?',
  driverPhotoUrl: 'string?',
  driverRating: 'number?',
  dropOffPhotoUrl: 'string?',
})
export type OrderTracking = typeof OrderTracking.infer

export const OrderTrackingResponse = responseData(OrderTracking)

export const FoodItem = type({
  'productId': 'string',
  'name': 'string',
  'basePrice': 'string',
  'quantity': 'number',
  'description?': 'string | null | undefined',
  'customizations?': 'unknown[]',
})
export type FoodItem = typeof FoodItem.infer

export const PaymentInfo = type({
  paymentIntentId: 'string?',
})
export type PaymentInfo = typeof PaymentInfo.infer

export const ShippingInfo = type({
  'email?': 'string | null | undefined',
  'name': 'string',
  'phone': 'string',
  'line1': 'string',
  'line2?': 'string | null | undefined',
  'city': 'string',
  'state': 'string',
  'postalCode': 'string',
  'country': 'string',
})
export type ShippingInfo = typeof ShippingInfo.infer

export const Quote2 = type({
  subtotal: 'string',
  deliveryFee: 'string',
  serviceFee: 'string',
  smallOrderFee: 'string',
  salesTax: 'string',
  deliveryTimeMin: 'number',
  deliveryTimeMax: 'number',
  totalWithoutTips: 'string',
  expectedTimeOfArrival: 'string',
})
export type Quote2 = typeof Quote2.infer

export const AddedFees2 = type({
  flatFee: 'string',
  percentFee: 'number',
  isFeeTaxable: 'boolean',
  totalFee: 'string',
  salesTax: 'string',
})
export type AddedFees2 = typeof AddedFees2.infer

export const Extras = type({
  storeId: 'string',
  store: 'string',
  storeAddress: 'string',
  quoteId: 'string',
  tip: 'string',
  totalWithTip: 'string',
  partnerOrderId: 'string?',
})
export type Extras = typeof Extras.infer

export const MoreInfo = type({
  'amountDiscount': 'string',
  'anonymous': 'boolean',
  'useWallet': 'boolean',
  'amountRoundUp': 'string',
  'refSrc': 'string',
  'note?': 'string | null | undefined',
})
export type MoreInfo = typeof MoreInfo.infer

export const Order = type({
  id: 'string',
  accountId: 'string',
  email: 'string',
  status: 'string',
  checkoutType: 'string',
  locale: 'string',
  foodItems: FoodItem.array(),
  paymentInfo: PaymentInfo,
  shippingInfo: ShippingInfo,
  quote: Quote2,
  addedFees: AddedFees2,
  extras: Extras,
  moreInfo: MoreInfo,
  createdAt: 'string',
  updatedAt: 'string',
  createdBy: 'string',
  updatedBy: 'string',
})

export type Order = typeof Order.infer

export const Orders = Order.array()
export type Orders = typeof Orders.infer

export const OrderResponse = responseData(Order)
export const FoodPayment = type({
  clientSecret: 'string',
  order: Order,
  paymentIntentStatus: 'string',
})

export type FoodPayment = typeof FoodPayment.infer

export const FoodPaymentResponse = responseData(FoodPayment)
